import { EventListeners, MinzeElement, Reactive } from "minze";
import { Locale } from "../../modules/locale";
import { EMAIL_REGEX, PHONE_REGEX } from "../../modules/utils";
import { submitForm } from "../../constants";
import { SquidexTextLabels } from "../../modules/squidex/labels";
import { SquidexAuth } from "../../modules/squidex/auth";

export interface TjPromotionalMaterialForm {
  errors: { [fieldName: string]: string };
  values: { [fieldName: string]: any };
  submitted: boolean;
  materials: { [key: string]: number };
}
interface Material {
  identifier: string;
  image: string;
  name: string;
  format: string;
  limit: number;
  soldOut: boolean;
}

let materials: Material[] = [];

const fetchMaterialList = (): Promise<any> => {
  const graphqlQuery = `
    query {
      materielPromotionnel: findMaterielPromotionnelSingleton {
        data {
          items {
            iv {
              data {
                name {
                  fr
                  en
                }
                format {
                  fr
                  en
                }
                identifier {
                  iv
                }
                image {
                  iv {
                    url
                  }
                }
                limit {
                  iv
                }
                soldOut {
                  iv
                }
              }
            }
          }
        }
      }
    }
  `;
  return new SquidexAuth()
    .getToken()
    .then((token) => {
      const fetchOptions = {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query: graphqlQuery }),
      };

      return fetch(
        "https://cloud.squidex.io/api/content/tel-jeunes/graphql",
        fetchOptions,
      )
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
        });
    })
    .catch((error) => {
      console.error(error);
    });
};

export class TjPromotionalMaterialForm extends MinzeElement {
  reactive: Reactive = [
    ["errors", {}],
    ["values", {}],
    ["submitted", false],
    ["materials", {}],
    ["buttonDisabled", true],
    ["loading", true],
    ["previewImage", null],
  ];
  isMobile = window.matchMedia("(max-width: 768px)").matches;
  onReady() {
    fetchMaterialList()
      .then((data) => {
        const list = data?.data?.materielPromotionnel?.data?.items?.iv;
        if (list && list.length > 0) {
          materials = list.map((item: any) => {
            return {
              identifier: item?.data?.identifier?.iv,
              image: item?.data?.image?.iv?.[0]?.url,
              name: item?.data?.name?.[Locale.language],
              format: item?.data?.format?.[Locale.language],
              limit: item?.data?.limit?.iv,
              soldOut: item?.data?.soldOut?.iv,
            };
          });
        } else {
          materials = [];
        }
        this.loading = false;
        this.checkIfButtonIsDisabled();
      })
      .catch((error) => {
        console.error(error);
      });
  }
  setValues = () => {
    const nameField = this.select(`#name`)?.getAttribute("value");
    const surnameField = this.select(`#surname`)?.getAttribute("value");
    const emailField = this.select(`#email`)?.getAttribute("value");
    const phoneField = this.select(`#phone`)?.getAttribute("value");
    const schoolField = this.select("#school")?.getAttribute("value");
    const addressField = this.select("#address")?.getAttribute("value");
    const cityField = this.select("#city")?.getAttribute("value");
    const provinceField = this.select("#province")?.getAttribute("value");
    const zipField = this.select("#zip")?.getAttribute("value");
    this.values = {
      name: nameField,
      surname: surnameField,
      email: emailField,
      phone: phoneField,
      school: schoolField,
      address: addressField,
      city: cityField,
      province: provinceField,
      zip: zipField,
    };
  };

  submitForm = async () => {
    this.submitted = false;
    this.errors = {};
    this.setValues();
    if (!this.values["name"] || this.values["name"] === "undefined")
      this.errors = {
        ...this.errors,
        name: SquidexTextLabels.getKey("promotionalMaterialForm.requiredError"),
      };
    if (!this.values["surname"] || this.values["surname"] === "undefined")
      this.errors = {
        ...this.errors,
        surname: SquidexTextLabels.getKey(
          "promotionalMaterialForm.requiredError",
        ),
      };
    if (!this.values["email"] || this.values["email"] === "undefined")
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey(
          "promotionalMaterialForm.requiredError",
        ),
      };
    else if (!EMAIL_REGEX.test(this.values["email"])) {
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey(
          "promotionalMaterialForm.invalidEmailError",
        ),
      };
    }
    if (!this.values["school"] || this.values["school"] === "undefined")
      this.errors = {
        ...this.errors,
        school: SquidexTextLabels.getKey(
          "promotionalMaterialForm.requiredError",
        ),
      };
    if (!this.values["address"] || this.values["address"] === "undefined")
      this.errors = {
        ...this.errors,
        address: SquidexTextLabels.getKey(
          "promotionalMaterialForm.requiredError",
        ),
      };
    if (!this.values["city"] || this.values["city"] === "undefined")
      this.errors = {
        ...this.errors,
        city: SquidexTextLabels.getKey("promotionalMaterialForm.requiredError"),
      };
    if (!this.values["province"] || this.values["province"] === "undefined")
      this.errors = {
        ...this.errors,
        province: SquidexTextLabels.getKey(
          "promotionalMaterialForm.requiredError",
        ),
      };
    if (!this.values["zip"] || this.values["zip"] === "undefined")
      this.errors = {
        ...this.errors,
        zip: SquidexTextLabels.getKey("promotionalMaterialForm.requiredError"),
      };
    if (
      this.values["phone"] &&
      this.values["phone"] !== "undefined" &&
      !PHONE_REGEX.test(this.values["phone"])
    ) {
      this.errors = {
        ...this.errors,
        phone: SquidexTextLabels.getKey("contactForm.invalidPhoneError"),
      };
    }
    if (!Object.values(this.errors).length) {
      const response = await submitForm("354gz0x", {
        ...this.values,
        ...this.materials,
      });
      if (response.status === "success") {
        this.submitted = true;
        this.values = {};
        this.materials = {};
        this.rerender();
      }
    } else {
      this.select(`#${Object.keys(this.errors)[0]}`)?.scrollIntoView();
      this.rerender();
    }
  };

  resetForm = () => {
    this.submitted = false;
    this.materials = {};
  };

  checkIfButtonIsDisabled = () => {
    let disabled = true;
    Object.values(this.materials).forEach((material) => {
      if (material > 0) disabled = false;
    });
    this.buttonDisabled = disabled;
    this.rerender(true)
  };

  removeMaterial = (event: Event) => {
    this.setValues();
    const removeButton = (event.target as Element)?.closest(
      ".remove-material",
    ) as Element | null;
    const materialId = removeButton?.getAttribute("key-id");
    if (materialId)
      this.materials[materialId] = this.materials[materialId]
        ? this.materials[materialId] - 1
        : 0;
    this.checkIfButtonIsDisabled();
  };

  addMaterial = (event: Event) => {
    this.setValues();
    const addButton = (event.target as Element)?.closest(
      ".add-material",
    ) as Element | null;
    if (addButton?.getAttribute("disabled") === "true") return;
    const materialId = addButton?.getAttribute("key-id");
    if (materialId)
      this.materials[materialId] = this.materials[materialId]
        ? this.materials[materialId] + 1
        : 1;
    this.checkIfButtonIsDisabled();
  };

  renderMaterial = ({
    identifier,
    image,
    name,
    format,
    limit,
    soldOut,
  }: Material) => {
    return `
      <tj-box
        width="${this.isMobile ? "100%" : "calc(50% - 4px)"}"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        background="#F6F6F6"
        padding="8px"
        borderRadius="12px"
      >
        <tj-box width="${this.isMobile ? "115px" : "25%"}" mr="8px" class="material-image-block" data-image="${image}">
          <img src="${image}" class="material-image" alt="" width="${this.isMobile ? "115px" : "100%"}"/>
        </tj-box>
        <tj-box padding="8px">
          <tj-box mb="4px">
            <tj-text color="#133762" font-size="16px" font-weight="500" line-height="24px">${name}</tj-text>
          </tj-box>
          <tj-box mb="10px">
            <tj-text color="#133762" font-size="12px">${format}</tj-text>
          </tj-box>
          ${
            !soldOut
              ? `<tj-box display="flex" alignItems="center" columnGap="16px">
            <tj-button
              width="32px"
              height="32px"
              padding="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="#FFFFFF"
              boxShadow="none"
              class="remove-material"
              key-id="${identifier}"
              disabled="${!this.materials[identifier]}"
            >
              <tj-minus-icon ${!this.materials[identifier] ? `data-disabled="true"` : ""}></tj-minus-icon>
            </tj-button>
            <tj-text color="#133762" font-size="16px">
              ${this.materials[identifier] || 0}
            </tj-text>
            <tj-box>
              <tj-button
                width="32px"
                height="32px"
                padding="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="#FFFFFF"
                boxShadow="none"
                class="add-material"
                key-id="${identifier}"
                disabled="${this.materials[identifier] >= limit}"
              >
                <tj-plus-icon ${this.materials[identifier] >= limit ? `data-disabled="true"` : ""}></tj-plus-icon>
              </tj-button>
            </tj-box>
          </tj-box>`
              : SquidexTextLabels.getKey(
                  "promotionalMaterialForm.materialUnavailable",
                )
          }
        </tj-box>
      </tj-box>
    `;
  };

  openPreview = (event: Event) => {
    this.setValues();
    const imageBlock = (event.target as Element)?.closest(
      ".material-image-block",
    ) as Element | null;
    this.previewImage = imageBlock?.getAttribute("data-image");
  };

  closePreview = () => {
    this.previewImage = null;
  };

  html = () => `
    ${
      this.previewImage
        ? `
      <div class="preview-image">
        <tj-button bg="transparent" boxShadow="none" id="close-preview">
          <tj-close-icon></tj-close-icon>
        </tj-button>
        <img src="${this.previewImage}.jpg" alt="" />
      </div>
    `
        : ""
    }
    ${
      this.submitted
        ? `
      <tj-box display="flex" flexDirection="column" alignItems="center">
        <tj-text text-align="center" color="#133762">${SquidexTextLabels.getKey("promotionalMaterialForm.successMessage")}</tj-text>
        <tj-box mt="48px">
          <tj-button id="return" size="small">${SquidexTextLabels.getKey("promotionalMaterialForm.successMessageButton")}</tj-button>
        </tj-box>
      </tj-box>
    `
        : `
      <tj-box mb="16px">
        <tj-text color="#133762" font-size="20px" font-weight="500">
          ${SquidexTextLabels.getKey("promotionalMaterialForm.contactInfoLabel")}
        </tj-text>
      </tj-box>
      <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
        <tj-input
          id="name"
          name="name"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.nameLabel")}"
          error="${this.errors["name"]}"
          value="${this.values["name"] || ""}"
          default-value="${this.values["name"] || ""}"
        ></tj-input>
        <tj-input
          id="surname"
          name="surname"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.surnameLabel")}"
          error="${this.errors["surname"]}"
          value="${this.values["surname"] || ""}"
          default-value="${this.values["surname"] || ""}"
        ></tj-input>
      </tj-box>
      <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
        <tj-input
          id="email"
          name="email"
          type="email"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.emailLabel")}"
          error="${this.errors["email"]}"
          value="${this.values["email"] || ""}"
          default-value="${this.values["email"] || ""}"
        ></tj-input>
        <tj-input
          id="phone"
          name="phone"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.phoneLabel")}"
          error="${this.errors["phone"]}"
          value="${this.values["phone"] || ""}"
          default-value="${this.values["phone"] || ""}"
        ></tj-input>
      </tj-box>
      <tj-box mt="${this.isMobile ? "24px" : "48px"}" mb="${this.isMobile ? "24px" : "48px"}">
        <tj-separator-icon></tj-separator-icon>
      </tj-box>
      <tj-box mb="16px">
        <tj-text color="#133762" font-size="20px" font-weight="500">
          ${SquidexTextLabels.getKey("promotionalMaterialForm.establishmentInfoLabel")}
        </tj-text>
      </tj-box>
      <tj-box mb="16px">
        <tj-input
          id="school"
          name="school"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.schoolLabel")}"
          error="${this.errors["school"]}"
          value="${this.values["school"] || ""}"
          default-value="${this.values["school"] || ""}"
        ></tj-input>
      </tj-box>
      <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
        <tj-input
          id="address"
          name="address"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.addressLabel")}"
          error="${this.errors["address"]}"
          value="${this.values["address"] || ""}"
          default-value="${this.values["address"] || ""}"
        ></tj-input>
        <tj-input
          id="city"
          name="city"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.cityLabel")}"
          error="${this.errors["city"]}"
          value="${this.values["city"] || ""}"
          default-value="${this.values["city"] || ""}"
        ></tj-input>
      </tj-box>
      <tj-box mb="16px" display="flex" flexDirection="${this.isMobile ? "column" : "row"}" rowGap="16px" columnGap="16px">
        <tj-input
          id="province"
          name="province"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.provinceLabel")}"
          error="${this.errors["province"]}"
          value="${this.values["province"] || ""}"
          default-value="${this.values["province"] || ""}"
        ></tj-input>
        <tj-input
          id="zip"
          name="zip"
          placeholder="${SquidexTextLabels.getKey("promotionalMaterialForm.zipLabel")}"
          error="${this.errors["zip"]}"
          value="${this.values["zip"] || ""}"
          default-value="${this.values["zip"] || ""}"
        ></tj-input>
      </tj-box>
      <tj-box mt="${this.isMobile ? "24px" : "48px"}" mb="${this.isMobile ? "24px" : "48px"}">
        <tj-separator-icon></tj-separator-icon>
      </tj-box>
      <tj-box mb="16px">
        <tj-text color="#133762" font-size="20px" font-weight="500">
          ${SquidexTextLabels.getKey("promotionalMaterialForm.materialInfoLabel")}
        </tj-text>
      </tj-box>
      <tj-box display="flex" flexWrap="wrap" columnGap="8px" rowGap="8px" mb="48px">
        ${materials
          .map(
            (material) => `
          ${this.renderMaterial({
            identifier: material.identifier,
            image: material.image,
            name: material.name,
            format: material.format,
            limit: material.limit,
            soldOut: material.soldOut,
          })}
        `,
          )
          .join("")}
      </tj-box>
      <tj-box mb="16px" display="flex" alignItems="center" justifyContent="center" flexDirection="column">
        <tj-button id="submit" name="save" class="promotional-material-form-submit-button"
        width="fit-content" fontWeight="700" disabled="${this.buttonDisabled ? true : false}">
          ${SquidexTextLabels.getKey("promotionalMaterialForm.send")}
        </tj-button>
      </tj-box>
    `
    }
  `;

  css = () => `
    :host {
      width: 100%;
      overflow-x: hidden;
    }
    .title {
      font-weight: 500;
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
      margin: 80px 0 8px 0;
    }
    .material-image {
      border-radius: 8px;
    }
    tj-minus-icon, tj-plus-icon {
      position: relative;
      bottom: 3px;
    }
    .material-image {
      cursor: pointer;
    }
    .preview-image {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #161616;
    }
    .preview-image img {
      height: 65vh;
    }
    #close-preview {
      position: fixed;
      top: 58px;
      right: 32px;
    }
    #submit {
      pointer-events: ${this.buttonDisabled ? "none" : "auto"};
    }
  `;
  eventListeners: EventListeners = [
    ["#submit", "click", this.submitForm.bind(this)],
    ["#return", "click", this.resetForm],
    [".remove-material", "click", this.removeMaterial.bind(this)],
    [".add-material", "click", this.addMaterial.bind(this)],
    [".material-image-block", "click", this.openPreview],
    ["#close-preview", "click", this.closePreview],
  ];
}
